type CustomValidationFn = (element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => void;

class FormGroup {
    constructor(
        private readonly element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
        private readonly controlElement: HTMLElement,
        private readonly groupElement: HTMLElement,
        private readonly callback: (control: FormGroup) => void,
        private readonly customValidators?: Array<CustomValidator>
    ) {
        let defaultEvent = 'blur';
        if (element instanceof HTMLSelectElement) {
            defaultEvent = 'change';
        } else if (element instanceof HTMLInputElement && (element.type === 'radio' || element.type === 'checkbox')) {
            defaultEvent = 'click';
        }

        const dataValidateOn = element.attributes.getNamedItem('data-validate-on');
        const validateOn = dataValidateOn ? dataValidateOn.value : defaultEvent;

        element.addEventListener(validateOn, (event) => {
            this.customValidation();
            if (element.validity) {
                controlElement.querySelectorAll('.invalid-feedback').forEach(
                    div => controlElement.removeChild(div)
                );
                if (element.validity.valid && !element.validity.customError) {
                    groupElement.classList.remove('error');
                } else {
                    groupElement.classList.add('error');
                    const invalidFeedback = document.createElement('div');
                    invalidFeedback.classList.add('invalid-feedback');
                    invalidFeedback.innerHTML = element.validationMessage;
                    controlElement.appendChild(invalidFeedback);
                }
                this.callback(this);
            }
        });
    }

    isValid(): boolean {
        this.customValidation();
        return this.element.validity && this.element.validity.valid && !this.element.validity.customError;
    }

    getName(): string | undefined {
        return this.element.name;
    }

    getValue(): any {
        if (this.element instanceof HTMLInputElement && (this.element.type === 'radio' || this.element.type === 'checkbox')) {
            const givenValue = this.element.value;
            if (givenValue !== 'on') {
                return this.element.checked ? givenValue : undefined;
            } else {
                return this.element.checked;
            }
        } else {
            return this.element.value;
        }
    }

    getElement(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
        return this.element;
    }

    isSelected(): boolean {
        if (this.element instanceof HTMLInputElement && (this.element.type === 'radio' || this.element.type === 'checkbox')) {
            return this.element.checked;
        } else {
            return true;
        }
    }

    private customValidation() {
        (this.customValidators || []).forEach(v => {
            v.fn(this.element);
        });
    }
}

type ValidityCallback = (valid: boolean) => void;

type CustomValidator = { type: 'name' | 'id', selector: string, fn: CustomValidationFn }

export class FormValidator {

    private readonly form: HTMLFormElement;
    private controls: Array<FormGroup> = [];
    private validityListeners: Array<ValidityCallback> = [];
    private readonly customValidators: Array<CustomValidator>;
    private valid = true;

    constructor(form: HTMLFormElement | string, listeners?: ValidityCallback | Array<ValidityCallback>, customValidators?: Array<CustomValidator>) {
        if (typeof form === 'string') {
            const element = document.querySelector<HTMLFormElement>(form);
            if (!element) {
                throw new Error(`could not find form element ${form}`);
            }
            this.form = element;
        } else {
            this.form = form;
        }
        this.validityListeners = this.validityListeners.concat(listeners || []);
        this.customValidators = customValidators || [];
        this.updateControls();
        this.validate();
    }

    updateControls(): void {
        this.controls = [];
        this.form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea').forEach(
            e => this.addControl(e)
        );
    }

    validate(): boolean {
        const oldValidity = this.valid;

        this.valid = this.controls.reduce((prev, cur) => {
            return prev && cur.isValid();
        }, true);
        if (oldValidity !== this.valid) {
            this.validityListeners.forEach(listener => listener(this.valid));
        }

        return this.valid;
    }

    getCurrentFormValue(): any {

        const names = this.controls.reduce((prev, cur) => {
            if (prev.indexOf(cur.getName()) === -1) {
                prev.push(cur.getName());
            }
            return prev;
        }, [])

        return names.reduce((prev, name) => {
            const controls = this.controls.filter(control => control.getName() === name && control.isSelected());
            if (controls.length > 0) {
                if (controls.length === 1) {
                    prev[controls[0].getName()] = controls[0].getValue();
                } else {
                    prev[controls[0].getName()] = controls.map(control => control.getValue());
                }
            }
            return prev;
        }, {});
    }


    addValidityListener(listener: ValidityCallback): void {
        this.validityListeners.push(listener);
        listener(this.valid);
    }

    removeValidityListener(listener: ValidityCallback): void {
        this.validityListeners = this.validityListeners.filter(l => l !== listener);
    }

    private addControl(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void {
        try {
            const controlElement = this.findParentWithClass(element, 'form-control');
            const groupElement = this.findParentWithClass(controlElement, 'form-group');
            const customValidators = this.customValidators.filter(v => {
                if (v.type === 'name') {
                    return v.selector === element.name;
                } else {
                    return v.selector === element.id;
                }
            })
            this.controls.push(new FormGroup(element, controlElement, groupElement, this.changed.bind(this), customValidators));
        } catch (e) {
            console.warn('error adding control', {error: e});
        }
    }

    private changed(_group: FormGroup): void {
        this.validate();
    }

    private findParentWithClass(element: HTMLElement, cssClass: string): HTMLElement {
        if (element === document.documentElement) {
            throw new Error(`Could not find parent with css class ${cssClass}`);
        }
        if (element.parentElement.classList.contains(cssClass)) {
            return element.parentElement;
        } else {
            return this.findParentWithClass(element.parentElement, cssClass);
        }
    }
}
